import styled from '@emotion/styled'
import { ExtraScript } from '@pubstack/common/src/extraScript'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useExtraScripts } from '~/api/admin/extra-scripts.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import { Tooltip } from '~/components/Tooltip'
import TableRow from '~/components/table/TableRow'
import { SiteExtraScriptEditModal } from '~/modules/adm/inventory/sites/extra-scripts/SiteExtraScriptEditModal'

export const ExtraScriptDateFormat = 'yyyy-MM-dd HH:mm:ss'
export const isoToLocal = (date: string) => {
  return DateTime.fromISO(date).toFormat(ExtraScriptDateFormat)
}

const Header = styled.div`
  display: flex;
  margin-bottom: 12px;
  & input[type='file'] {
    display: none;
  }
`

const ActionBar = styled.form`
  display: flex;
  flex: 1 0 auto;
  justify-content: end;
`

const FilterAction = styled.form`
  display: flex;
`
const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const ActionsTableCell = styled(TableCell)`
  width: 0px; /** necessary to get the last cell to shrink down to hug action buttons */
`

export const SiteExtraScript: FunctionComponent = () => {
  // An extra script is code served on this media's own pages, so adding or changing one is the owner's.
  // The list stays readable: knowing what already runs is exactly what a `user` comes here for.
  const { canEdit, disabledReason } = useScopePermissions()
  const { control } = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  })
  const search = useWatch({ control, name: 'search' })
  const modal = useGlobalModal()
  const { siteId } = useParams()
  const { data, isFetching } = useExtraScripts(siteId!)
  const extraScripts = data || []
  const [columns, setColumns] = useState<TableColumns<ExtraScript>>([
    {
      name: 'File name',
      attributeSort: 'name',
      isSortable: true,
      order: 'none',
    },
    {
      name: 'date',
      attributeSort: 'date',
      isSortable: true,
      order: 'descending',
    },
    {
      name: 'Early start',
      isSortable: false,
    },
    {
      name: 'Synchronous',
      isSortable: false,
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const displayedExtraScripts = [
    ...handleTableSearchAndSort(
      columns,
      extraScripts.filter((e) => e.enabled),
      search,
      ['name']
    ),
    ...handleTableSearchAndSort(
      columns,
      extraScripts.filter((e) => !e.enabled),
      search,
      ['name']
    ),
  ]

  const openEditModal = (extraScript?: ExtraScript) => {
    modal.open(SiteExtraScriptEditModal, { extraScript })
  }

  return (
    <>
      <Header>
        <FilterAction>
          <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
        </FilterAction>
        <ActionBar>
          <Tooltip title={disabledReason} disabled={canEdit}>
            <Button variant={'primary'} disabled={!canEdit} onClick={() => openEditModal()} iconName={'upload'} trackingLabel={'adm-sites-stacks-extra-script-new'}>
              New extra-script
            </Button>
          </Tooltip>
        </ActionBar>
      </Header>

      <Table columns={columns} isLoading={isFetching ?? false} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {displayedExtraScripts.map((extraScript) => (
          <TableRow key={`${extraScript.name}-${extraScript.date}`} archived={!extraScript.enabled}>
            <TableCell>{extraScript.name}</TableCell>
            <TableCell>{extraScript.date ? isoToLocal(extraScript.date) : ''}</TableCell>
            <TableCell>{extraScript.enabled && <Icon name={extraScript.beforeStart ? 'check' : 'close'} />}</TableCell>
            <TableCell>{extraScript.enabled && <Icon name={extraScript.synchronous ? 'check' : 'close'} />}</TableCell>
            <ActionsTableCell>
              <ButtonActions>
                <a key={extraScript.url} href={extraScript.url} download target={'_blank'} rel={'noreferrer'}>
                  <Button variant={'tertiary'} iconName={'open_in_new'} onClick={(event) => event.stopPropagation()} trackingLabel={'adm-sites-stacks-extra-script-download'} />
                </a>
                <Tooltip title={canEdit ? 'Edit' : disabledReason}>
                  <Button iconName={'edit'} variant={'tertiary'} title={'Edit'} disabled={!canEdit} onClick={() => openEditModal(extraScript)} trackingLabel={'adm-sites-stacks-extra-script-edit'} />
                </Tooltip>
              </ButtonActions>
            </ActionsTableCell>
          </TableRow>
        ))}
      </Table>
    </>
  )
}
